import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDepartments, checkUserCanManageDepartments, toggleDepartmentVisibility } from '../../services/departmentService';
import { Department } from '../../types/models';
import { Plus, Building2, Search, Download, FileSpreadsheet, FileText, File as FilePdf, Eye, EyeOff, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { exportToExcel, exportToCSV, exportToPDF } from '../../utils/export';
import { useAuthStore } from '../../store/authStore';

const DepartmentList: React.FC = () => {
  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState<Department | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [canManageDepartments, setCanManageDepartments] = useState(false);
  const { t } = useTranslation('common');
  const { user } = useAuthStore();

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [departmentsData, canManage] = await Promise.all([
          getDepartments(),
          checkUserCanManageDepartments()
        ]);
        setDepartments(departmentsData);
        setCanManageDepartments(canManage);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleExport = (format: 'excel' | 'csv' | 'pdf') => {
    if (!selectedDepartment) return;
    
    const departmentStudents = selectedDepartment.students || [];
    switch (format) {
      case 'excel':
        exportToExcel(departmentStudents);
        break;
      case 'csv':
        exportToCSV(departmentStudents);
        break;
      case 'pdf':
        exportToPDF(departmentStudents);
        break;
    }
    setShowExportMenu(false);
  };

  const handleToggleVisibility = async (departmentId: string, currentVisibility: boolean) => {
    try {
      await toggleDepartmentVisibility(departmentId, !currentVisibility);
      
      // Update the departments list with the new visibility status
      setDepartments(prevDepartments => 
        prevDepartments.map(dept => 
          dept.department_id === departmentId 
            ? { ...dept, hidden_in_exports: !currentVisibility } 
            : dept
        )
      );
    } catch (error) {
      console.error('Error toggling department visibility:', error);
    }
  };

  const filteredDepartments = departments.filter(department =>
    department.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (department.name_tamil || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between">
        <h1 className="text-2xl font-bold text-gray-900">{t('departments.title')}</h1>
        {user && canManageDepartments && (
          <Link
            to="/departments/new"
            className="mt-3 md:mt-0 inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <Plus className="h-4 w-4 mr-2" />
            {t('departments.actions.addDepartment')}
          </Link>
        )}
      </div>

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <div className="flex flex-col md:flex-row space-y-3 md:space-y-0 md:space-x-4 md:items-center">
            <div className="flex-1 relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('departments.filters.searchPlaceholder')}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              />
            </div>
          </div>
        </div>
        
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        ) : filteredDepartments.length === 0 ? (
          <div className="text-center py-12">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('departments.noDepartments.title')}</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm ? t('departments.noDepartments.description.withFilters') : t('departments.noDepartments.description.noFilters')}
            </p>
            {user && canManageDepartments && (
              <div className="mt-6">
                <Link
                  to="/departments/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('departments.actions.addDepartment')}
                </Link>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('departments.table.name')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('departments.table.tamilName')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('departments.table.students')}
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {t('departments.table.visibility')}
                  </th>
                  <th scope="col" className="relative px-6 py-3">
                    <span className="sr-only">{t('departments.table.actions')}</span>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredDepartments.map((department) => (
                  <tr key={department.department_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <Link
                        to={`/departments/${department.department_id}/details`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-800 cursor-pointer flex items-center"
                      >
                        <Building2 className="h-4 w-4 mr-2 text-blue-500" />
                        {department.name}
                      </Link>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {department.name_tamil || '-'}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {department.students?.length || 0} {t('departments.table.students')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleToggleVisibility(department.department_id, department.hidden_in_exports || false)}
                        className={`inline-flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded ${
                          department.hidden_in_exports 
                            ? 'text-gray-700 bg-gray-200 hover:bg-gray-300' 
                            : 'text-green-700 bg-green-100 hover:bg-green-200'
                        }`}
                      >
                        {department.hidden_in_exports ? (
                          <>
                            <EyeOff className="h-4 w-4 mr-1" />
                            {t('departments.visibility.hidden')}
                          </>
                        ) : (
                          <>
                            <Eye className="h-4 w-4 mr-1" />
                            {t('departments.visibility.visible')}
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-3">
                        <Link
                          to={`/departments/${department.department_id}/details`}
                          className="text-blue-600 hover:text-blue-800 flex items-center"
                        >
                          <ExternalLink className="h-4 w-4 mr-1" />
                          {t('familyConnections.view')}
                        </Link>
                        {user && canManageDepartments && (
                          <Link to={`/departments/${department.department_id}/edit`} className="text-indigo-600 hover:text-indigo-800">
                            {t('departments.actions.edit')}
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {selectedDepartment && (
              <div className="mt-8 px-6 pb-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {selectedDepartment.name} {t('departments.table.students')}
                  </h2>
                  <div className="relative">
                    <button
                      onClick={() => setShowExportMenu(!showExportMenu)}
                      className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {t('departments.actions.export')}
                    </button>
                    
                    {showExportMenu && (
                      <div className="absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-10">
                        <div className="py-1" role="menu">
                          <button
                            onClick={() => handleExport('excel')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <FileSpreadsheet className="h-4 w-4 mr-2" />
                            {t('participants.export.toExcel')}
                          </button>
                          <button
                            onClick={() => handleExport('csv')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <FileText className="h-4 w-4 mr-2" />
                            {t('participants.export.toCSV')}
                          </button>
                          <button
                            onClick={() => handleExport('pdf')}
                            className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                          >
                            <FilePdf className="h-4 w-4 mr-2" />
                            {t('participants.export.toPDF')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-white shadow overflow-hidden sm:rounded-lg">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.name')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.tamilName')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.dateOfBirth')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.gender')}
                        </th>
                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('participants.table.house')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(selectedDepartment.students || []).map((student) => (
                        <tr key={student.student_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Link to={`/participants/${student.student_id}`}>
                              <div className="text-sm font-medium text-blue-600 hover:text-blue-800">
                                {student.first_name} {student.last_name}
                              </div>
                            </Link>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {student.first_name_tamil} {student.last_name_tamil}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {format(new Date(student.date_of_birth), 'dd.MM.yyyy')}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              student.gender === 'M' ? 'bg-blue-100 text-blue-800' : 'bg-pink-100 text-pink-800'
                            }`}>
                              {student.gender === 'M' ? t('participants.filters.male') : t('participants.filters.female')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div 
                                className="h-3 w-3 rounded-full mr-2" 
                                style={{ backgroundColor: student.house?.house_color }}
                              />
                              <div className="text-sm text-gray-900">
                                {student.house?.house_name}
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DepartmentList;